import { AutogenLayoutOfficeRecurringPayAdjustment } from "./autogen/AutogenLayoutOfficeRecurringPayAdjustment";
import {
    DataHeader, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent,
    DataSourceValidationEvent, DropdownSelectionEvent, LookupModelSelectionEvent, PrintableEvent, Snackbar,
    ValidationResult
} from "@mcleod/components";
import { Api, HorizontalAlignment, LogManager, StringUtil, CurrencyUtil } from "@mcleod/core";
import { AutoMatchProfile } from "@mcleod/dispatch/src/AutoMatchProfile";

const log = LogManager.getLogger("lme.settlement.officepayroll.OfficeRecurringPayAdjustment");

export class OfficeRecurringPayAdjustment extends AutogenLayoutOfficeRecurringPayAdjustment {
    onLoad(): void | Promise<void> {
        CurrencyUtil.configureCurrencySelector(this.textboxCurrency);
        this.textboxGlid.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            if (mode === DataSourceMode.SEARCH) {
                return null;
            }
            const result: ValidationResult = {
                component: this.textboxGlid,
                isValid: true
            };
            const lookupModelData = this.textboxGlid.getFirstLookupModelData();
            const isEscrowGlAccount: boolean = lookupModelData?.getBoolean("is_escrow", false);
            const isEscrowDeductionType = this.activeRow.get("deduction_type") === "T" || this.activeRow.get("deduction_type") === "F";
            if (isEscrowDeductionType && !isEscrowGlAccount) {
                result.isValid = false;
                result.validationMessage = result.caption = "GL account must be an escrow account for Escrow type recurring adjustments.";
            }
            const isEarningAdjustmentType = this.activeRow.get("deduct_code.code_type") === "E" || this.activeRow.get("deduct_code.code_type") === "P";
            if (isEarningAdjustmentType && isEscrowGlAccount) {
                result.isValid = false;
                result.validationMessage = result.caption = "Cannot use escrow GL account with Earning or Per Diem type adjustments.";
            }
            return result;
        };
        this.textboxGlid.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.textboxGlid.validateSimple();
            }
        });
        this.layoutTableComment.displayCopyOrderFields = false;
        this.setComponentListeners();
        this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            this.validateEscrowAdjustments(event);
            this.validateRateEntry(event);
        });
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (this.activeRow.get("deduction_type") === "T" || this.activeRow.get("deduction_type") === "F") {
                return this.validateOverlappingEscrowDeductions();
            }
        };
        this.textboxFrequency.addlValidationCallback = () => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                return null;
            }
            const result: ValidationResult = {
                component: this.textboxFrequency,
                isValid: true
            };
            const frequency = this.activeRow.get("frequency");
            const codeType = this.activeRow.get("deduct_code.code_type");

            if (!frequency || !codeType) {
                return result;
            }

            // Copy logic from EntryAbstractRecurDeduct.checkFrequencyCodeConflict()
            // 1) Percent of net cannot be 'Before Tax' type
            if (frequency === "N" && codeType === "B") {
                result.isValid = false;
                result.validationMessage = result.caption = "Percent of net cannot be 'Before Tax' type";
                return result;
            }
            // 2) Reimbursement type not compatible with selected frequency (percentage-based)
            if ((frequency === "N" || frequency === "G" || frequency === "O") && codeType === "R") {
                result.isValid = false;
                result.validationMessage = result.caption = "Reimbursement type not compatible with selected frequency";
                return result;
            }
            // 3) Earning type not compatible with selected frequency (percent of net/gross)
            if ((frequency === "N" || frequency === "G") && codeType === "E") {
                result.isValid = false;
                result.validationMessage = result.caption = "Earning type not compatible with selected frequency";
                return result;
            }
            // 4) Only after-tax deductions are allowed for garnishments
            if ((frequency === "R" || frequency === "O") && codeType !== "D") {
                result.isValid = false;
                result.validationMessage = result.caption = "Only after-tax deductions are allowed for garnishments.";
                return result;
            }

            return result;
        }
    }

    private setComponentListeners() {
        this.textboxCurrency.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.changeCurrencyCode();
            }
        });
        this.textboxTotalToDate.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxGarnishCurrent.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxGarnishNetAmt.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxLoanBalance.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxOrigLoanAmt.addBlurListener(() => {
            this.changeCurrencyCode();
        });
        this.textboxGarnishArrears.addBlurListener(() => {
            this.changeCurrencyCode();
        });
    }

    sourceOffRecurDeductAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.onDeductCodeIdSelection();
        }
    }

    sourceOffRecurDeductAfterModeChange(_event: DataSourceModeChangeEvent) {
        if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.UPDATE) {
            this.onFrequencySelection();
            this.onDeductionTypeSelection();
            if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                if (this.textboxCurrency.visible && this.textboxCurrency.enabled) {
                    this.textboxCurrency.enabled = this.activeRow.isNull("last_taken") || (!this.activeRow.isNull("last_taken") && this.activeRow.isNull("currency"));
                    if (!this.textboxCurrency.enabled) {
                        this.textboxCurrency.disabledTooltip = "Currency cannot be updated because pay adjustments have already been created from this record.";
                    }
                }
            }
        }
    }

    textboxPayeeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            const selection = event.selection;
            this.activeRow.setValueAndLookupModelData("currency", selection.get("currency"), selection.getLookupModelData("currency"));
            this.changeCurrencyCode();
        }
    }

    private changeCurrencyCode() {
        if (!this.textboxCurrency.visible) return;
        const currencyCode = this.activeRow.get("currency");
        const [totalToDate, garnishNetAmount, garnishCurrent, arrearsAmount, loanBalance, originalLoanAmount, escrowThreshold] = [
            this.activeRow.getCurrency("total_to_date"),
            this.activeRow.getCurrency("garnish_net_amt"),
            this.activeRow.getCurrency("garnish_current"),
            this.activeRow.getCurrency("garnish_arrears"),
            this.activeRow.getCurrency("loan_balance"),
            this.activeRow.getCurrency("orig_loan_amt"),
            this.activeRow.getCurrency("escrow_threshold")
        ];

        if (totalToDate) {
            totalToDate.setCurrencyCode(currencyCode);
            this.activeRow.set("total_to_date", totalToDate);
        }

        if (garnishNetAmount) {
            garnishNetAmount.setCurrencyCode(currencyCode);
            this.activeRow.set("garnish_net_amt", garnishNetAmount);
        }

        if (garnishCurrent) {
            garnishCurrent.setCurrencyCode(currencyCode);
            this.activeRow.set("garnish_current", garnishCurrent);
        }

        if (arrearsAmount) {
            arrearsAmount.setCurrencyCode(currencyCode);
            this.activeRow.set("garnish_arrears", arrearsAmount);
        }

        if (loanBalance) {
            loanBalance.setCurrencyCode(currencyCode);
            this.activeRow.set("loan_balance", loanBalance);
        }

        if (originalLoanAmount) {
            originalLoanAmount.setCurrencyCode(currencyCode);
            this.activeRow.set("orig_loan_amt", originalLoanAmount);
        }

        if (escrowThreshold) {
            escrowThreshold.setCurrencyCode(currencyCode);
            this.activeRow.set("escrow_threshold", escrowThreshold);
        }
    }

    textboxDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            const selection = event.selection;
            this.activeRow.set("deduct_code.code_type", selection.get("code_type"));
            this.activeRow.set("glid", selection.get("glid"));
            AutoMatchProfile.applyLookupModelData(this.textboxGlid, this.activeRow);
            this.activeRow.set("amount", selection.getCurrency("amount")?.amount);
            this.onDeductCodeIdSelection();
            // Re-validate frequency rules that depend on code type (e.g., garnishment after-tax requirement)
            this.textboxFrequency.validateSimple();
            this.activeRow.set("short_desc", this.activeRow.getFirstLookupModelData("deduct_code_id")?.get("short_desc"));
        }
    }

    onDeductCodeIdSelection() {
        this.textboxLoanBalance.required = this.activeRow.get("deduction_type") === "L";
    }

    textboxFrequencyAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            if (this.mainDataSource.mode !== DataSourceMode.ADD && (event.selection.value === "O" || event.selection.value === "R")) {
                Api.search("lme/settlement/validate-pay-adjustment-frequency", { recurring_pay_adjustment_id: this.activeRow.get("id"), is_office: true }).then((response) => {
                    const errorMessage = response.data?.[0]?.error_message;
                    if (errorMessage) {
                        Snackbar.showWarningSnackbar(errorMessage, { targetPanel: this });
                        this.activeRow.set("frequency", this.activeRow.originalData?.["frequency"]);
                    } else {
                        this.onFrequencySelection();
                    }
                });
            } else {
                this.onFrequencySelection();
            }
        }
    }

    onFrequencySelection() {
        this.showGarnishments();
        this.textboxCycleId.required = this.textboxCycleId.enabled = this.activeRow.get("frequency") === "C" || this.activeRow.get("frequency") === "R";
        if (!this.textboxCycleId.required) {
            this.activeRow.set("cycle_id", null);
        }
        this.textboxAmount.required = this.textboxAmount.enabled = !(this.activeRow.get("frequency") === "G" || this.activeRow.get("frequency") === "N" || this.activeRow.get("frequency") === "O");
        if (!this.textboxAmount.required) {
            this.activeRow.set("amount", null);
        }
        this.textboxPercentage.required = this.textboxPercentage.enabled = this.activeRow.get("frequency") === "G" || this.activeRow.get("frequency") === "N" || this.activeRow.get("frequency") === "O";
        if (!this.textboxPercentage.required) {
            this.activeRow.set("percentage", null);
        }
        if (this.activeRow.get("frequency") === "O") {
            this.textboxPercentage.required = false;
        }
    }

    showGarnishments() {
        this.tabGarnishments.visible = this.activeRow.get("frequency") === "O" || this.activeRow.get("frequency") === "R";
        this.textboxGarnishPriority.required = this.tabGarnishments.visible;
        this.textboxVendorId.required = this.tabGarnishments.visible;
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
    }

    textboxDeductionTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            this.onDeductionTypeSelection();
        }
    }

    onDeductionTypeSelection() {
        this.textboxLoanBalance.enabled = this.textboxLoanBalance.required = this.textboxOrigLoanAmt.enabled = this.activeRow.get("deduction_type") === "L";
        if (!this.textboxLoanBalance.enabled) {
            this.activeRow.set("loan_balance", null);
            this.activeRow.set("orig_loan_amt", null);
        }
    }

    private validateEscrowAdjustments(event: DataSourceValidationEvent) {
        const deductionType = this.activeRow.get("deduction_type");
        const payAdjustmentType = this.activeRow.get("deduct_code.code_type");
        const frequency = this.activeRow.get("frequency");
        const glAccountData = this.textboxGlid.getFirstLookupModelData();
        const isEscrowGLAccount = glAccountData?.getBoolean("is_escrow", false);
        const escrowThreshold = this.activeRow.getCurrency("escrow_threshold");

        if (deductionType === "T" || deductionType === "F") {
            if (payAdjustmentType !== "B" && payAdjustmentType !== "D") {
                event.validationResults.push({
                    component: this.textboxDeductCodeId,
                    isValid: false,
                    caption: "Pay adjustment type must be before-tax or after-tax deduction for an Escrow adjustment type."
                });
            }
        }

        if ((deductionType === "S" || deductionType === "L") && isEscrowGLAccount) {
            if (payAdjustmentType === "E" || payAdjustmentType === "P") {
                event.validationResults.push({
                    component: this.textboxDeductCodeId,
                    isValid: false,
                    caption: "Cannot use an escrow GL account with Earning or Per Diem type deductions."
                });
            }
            if (frequency === "N" || frequency === "R" || frequency === "O") {
                event.validationResults.push({
                    component: this.textboxFrequency,
                    isValid: false,
                    caption: "Frequency is not valid when an escrow GL account is used."
                });
            }
        }
    }

    private async validateOverlappingEscrowDeductions(): Promise<boolean> {
        const payeeId = this.activeRow.get("payee_id");
        const glId = this.activeRow.get("glid");
        const startDate = this.activeRow.get("deduct_start_date");
        const endDate = this.activeRow.get("deduct_end_date");
        const currentRecordId = this.activeRow.get("id");

        if (!payeeId || !glId) {
            return;
        }

        const searchParams: any = {
            payee_id: payeeId,
            glid: glId,
            deduct_start_date: startDate,
            deduct_end_date: endDate
        };

        if (currentRecordId) {
            searchParams.current_record_id = currentRecordId;
        }

        const response = await Api.search("lme/settlement/validate-escrow-overlap", searchParams);
        if (response.data && response.data.length > 0) {
            const validationError = response.data[0];
            Snackbar.showWarningSnackbar(validationError.error_message, { targetPanel: this });
            return false;
        }
        return true;
    }

    private validateRateEntry(event: DataSourceValidationEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;

        const frequency: string = this.activeRow.get("frequency");
        if (frequency !== "O") return; // Only applies to Garnishment - Other

        const percent = this.activeRow.get("percentage");
        const garnishPercent = this.activeRow.get("garnish_percent");

        const hasPercent: boolean = !StringUtil.isEmptyString(percent);
        const hasGarnishNetAmt: boolean = !this.activeRow.isNull("garnish_net_amt"); // presence is enough (amount can be 0)
        const hasGarnishPercent: boolean = !StringUtil.isEmptyString(garnishPercent);

        if (!(hasPercent || hasGarnishNetAmt || hasGarnishPercent)) {
            event.validationResults.push({
                component: this.textboxPercentage,
                isValid: false,
                caption: "At least one of the following fields must be entered: Percent, Percent Limitation, or Guaranteed Disposable Income."
            });
        }
    }

    textboxEscrowBalanceOnPrintable(event: PrintableEvent) {
        event.printableLabel.align = HorizontalAlignment.LEFT;
    }
}
